import EmailIcon from '@mui/icons-material/AlternateEmail';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Link as MUILink,
	Paper,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

import HomeRoundedIcon from '@mui/icons-material/HomeRounded';

import logo from '../../common/assets/images/logo5.png';
import AuthService from '../../services/authService';
import LanguageSwitcher from '../../common/LanguageSwitcher/LanguageSwitcher';
import Constants from '../../common/constants';
import './login.scss';

function LoginPage() {
	const { t } = useTranslation();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors
	} = useForm();

	const [cookies, setCookie] = useCookies(['user']);
	const navigate = useNavigate();

	const captchaRef = useRef(null);
	const [captchaDone, setCaptchaDone] = useState(!Constants.RECAPTCHA_SITE_KEY);

	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [globalError, setGlobalError] = useState('');

	const onSubmit = async data => {
		const captchaToken = Constants.RECAPTCHA_SITE_KEY ? captchaRef.current?.getValue() || null : null;
		if (Constants.RECAPTCHA_SITE_KEY && !captchaToken) {
			setGlobalError(t('login.captchaRequired'));
			return;
		}
		setLoading(true);
		setGlobalError('');
		clearErrors();

		try {
			await AuthService.login({ ...data, captchaToken });

			const response = await AuthService.getUserInfo();
			setCookie('user', response.data);

			const roles = response.data?.roles?.map(r => r.authority) ?? [];
			if (roles.includes('ADMIN') || roles.includes('MODERATOR')) {
				navigate('/admin');
			} else if (roles.includes('MANAGER')) {
				navigate('/manager');
			} else {
				navigate('/');
			}
		} catch (e) {
			const msg =
				e?.response?.data?.message ||
				e?.message ||
				t('login.errorDefault');
			setGlobalError(msg);
			setError('email', { type: 'manual', message: '' });
			setError('password', { type: 'manual', message: '' });
			captchaRef.current?.reset();
			setCaptchaDone(false);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Wrapper>
			<Card elevation={3}>
				<Box sx={{ position: 'absolute', top: 10, right: 12, display: 'flex', alignItems: 'center', gap: 1.5 }}>
					<LanguageSwitcher variant='light' />
					<MUILink
						component={Link}
						to='/'
						underline='hover'
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.75,
							fontWeight: 700,
							fontSize: 13,
							color: 'text.secondary',
							'&:hover': { color: 'primary.main' }
						}}
					>
						<HomeRoundedIcon fontSize='small' />
						{t('common.toMainPage')}
					</MUILink>
				</Box>

				<Stack spacing={2.5}>
					{/* Лого и заголовок */}
					<Stack spacing={1} alignItems='center' textAlign='center'>
						<Link to='/' style={{ textDecoration: 'none' }}>
							<Logo src={logo} alt='Dulaty University' />
						</Link>
						<Typography variant='h5' sx={{ fontWeight: 800 }}>
							{t('login.title')}
						</Typography>
						<Typography variant='body2' color='text.secondary'>
							{t('login.subtitle')}
						</Typography>
					</Stack>

					{globalError && <Alert severity='error'>{globalError}</Alert>}

					<Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate>
						<Stack spacing={1.5}>
							<TextField
								label={t('login.email')}
								fullWidth
								autoComplete='username'
								error={!!errors.email}
								helperText={errors.email?.message}
								{...register('email', {
									required: t('login.emailRequired')
								})}
								InputProps={{
									startAdornment: (
										<InputAdornment position='start'>
											<EmailIcon fontSize='small' />
										</InputAdornment>
									)
								}}
							/>

							<TextField
								label={t('login.password')}
								fullWidth
								type={showPassword ? 'text' : 'password'}
								autoComplete='current-password'
								error={!!errors.password}
								helperText={errors.password?.message}
								{...register('password', {
									required: t('login.passwordRequired')
								})}
								InputProps={{
									startAdornment: (
										<InputAdornment position='start'>
											<LockIcon fontSize='small' />
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position='end'>
											<IconButton
												onClick={() => setShowPassword(s => !s)}
												edge='end'
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									)
								}}
							/>

							<Stack
								direction='row'
								alignItems='center'
								justifyContent='space-between'
								sx={{ mt: 0.5 }}
							>
								<FormControlLabel
									control={<Checkbox size='small' color='primary' />}
									label={<Typography variant='body2'>{t('login.rememberMe')}</Typography>}
								/>
								<MUILink component={Link} to='/forgot-password' variant='body2' underline='hover'>
									{t('login.forgotPassword')}
								</MUILink>
							</Stack>

							{Constants.RECAPTCHA_SITE_KEY && (
								<Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
									<ReCAPTCHA
										ref={captchaRef}
										sitekey={Constants.RECAPTCHA_SITE_KEY}
										onChange={token => setCaptchaDone(!!token)}
										onExpired={() => setCaptchaDone(false)}
									/>
								</Box>
							)}

							<Button
								type='submit'
								variant='contained'
								color='primary'
								size='large'
								fullWidth
								disabled={loading || !captchaDone}
								sx={{ mt: 0.5 }}
							>
								{loading ? (
									<>
										<CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} />
										{t('login.submitting')}
									</>
								) : (
									t('login.submit')
								)}
							</Button>
						</Stack>
					</Box>
				</Stack>
			</Card>
		</Wrapper>
	);
}

/* ===== Стили ===== */

const Wrapper = styled(Box)(({ theme }) => ({
	minHeight: '100vh',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: theme.spacing(2),
	background:
		theme.palette.mode === 'dark'
			? alpha(theme.palette.background.default, 0.95)
			: alpha(theme.palette.primary.light, 0.05)
}));

const Card = styled(Paper)(({ theme }) => ({
	width: '100%',
	maxWidth: 420,
	borderRadius: 16,
	padding: theme.spacing(3),
	position: 'relative',
	overflow: 'hidden',
	backgroundColor:
		theme.palette.mode === 'dark'
			? alpha(theme.palette.background.paper, 0.92)
			: theme.palette.background.paper,
	boxShadow: theme.shadows[3],
	border: `1px solid ${theme.palette.divider}`,
	'&::before': {
		content: '""',
		position: 'absolute',
		left: 0,
		top: 0,
		bottom: 0,
		width: 6,
		background:
			theme.palette.mode === 'dark'
				? alpha(theme.palette.primary.light, 0.7)
				: alpha(theme.palette.primary.main, 0.9)
	}
}));

const Logo = styled('img')(({ theme }) => ({
	width: 72,
	height: 72,
	objectFit: 'contain',
	filter: theme.palette.mode === 'dark' ? 'none' : 'saturate(1.05)',
	userSelect: 'none'
}));

export default LoginPage;
